/**
 * SettingsScreen.tsx — v4 Settings (S4)
 *
 * Left nav switches panels (no scroll-jump). ?panel= deep links honored.
 */

import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Building2, User, Image as ImageIcon, Sliders,
  CreditCard, FileText, Link2, Shield, BookOpen, Bell, Wallet, Users, UserPlus, Lock,
  type LucideIcon,
} from 'lucide-react'
import { loadSettings, type CompanySettings } from '../../api/settings'
import { useAuth } from '../../context/AuthContext'

import CompanyPanel        from './panels/CompanyPanel'
import ContactPanel        from './panels/ContactPanel'
import LogoSignaturePanel  from './panels/LogoSignaturePanel'
import ProjectDefaultsPanel from './panels/ProjectDefaultsPanel'
import CashPanel           from './panels/CashPanel'
import PaymentsPanel       from './panels/PaymentsPanel'
import SubscriptionPanel   from './panels/SubscriptionPanel'
import IntegrationsPanel   from './panels/IntegrationsPanel'
import AccountPanel        from './panels/AccountPanel'
import AccountingPanel     from './panels/AccountingPanel'
import NotificationsPanel  from './panels/NotificationsPanel'
import OwnersPanel         from './panels/OwnersPanel'
import TeamPanel           from './panels/TeamPanel'
import AdminPanel          from './panels/AdminPanel'

import './SettingsScreen.css'

const NAV_ITEMS: { id: string; label: string; Icon: LucideIcon; adminOnly?: boolean }[] = [
  { id: 'company',      label: 'Company',          Icon: Building2  },
  { id: 'team',         label: 'Team',              Icon: UserPlus   },
  { id: 'contact',      label: 'Contact',           Icon: User       },
  { id: 'owners',       label: 'Owners',            Icon: Users      },
  { id: 'branding',     label: 'Logo & Signature',  Icon: ImageIcon  },
  { id: 'defaults',     label: 'Project Defaults',  Icon: Sliders    },
  { id: 'cash',         label: 'Cash',              Icon: Wallet     },
  { id: 'payments',     label: 'Payments',          Icon: CreditCard },
  { id: 'billing',      label: 'Subscription',      Icon: FileText   },
  { id: 'integrations', label: 'Integrations',      Icon: Link2      },
  { id: 'notifications', label: 'Notifications',    Icon: Bell       },
  { id: 'accounting',   label: 'Accounting',        Icon: BookOpen   },
  { id: 'admin',        label: 'Admin',             Icon: Lock, adminOnly: true },
  { id: 'account',      label: 'Account',           Icon: Shield     },
]

export default function SettingsScreen() {
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const isAdmin = !!user?.is_admin
  const canManageSettings = user?.company_role === 'owner' || user?.company_role === 'admin'
  const navItems = NAV_ITEMS.filter((item) => {
    if (item.adminOnly && !isAdmin) return false
    if (!canManageSettings && item.id !== 'account') return false
    return true
  })
  const queryClient = useQueryClient()
  const { data: settings, isLoading, refetch } = useQuery<CompanySettings>({
    queryKey: ['settings'],
    queryFn: loadSettings,
    staleTime: 60_000,
  })

  const [activeId, setActiveId] = useState<string>(NAV_ITEMS[0].id)
  const effectiveId = navItems.some((item) => item.id === activeId)
    ? activeId : (navItems[0]?.id ?? null)

  useEffect(() => {
    if (isLoading) return
    const panel = searchParams.get('panel')
    if (
      panel
      && NAV_ITEMS.some(
        (n) => n.id === panel
          && (!n.adminOnly || isAdmin)
          && (canManageSettings || n.id === 'account'),
      )
    ) {
      setActiveId(panel)
    }
  }, [searchParams, isLoading, isAdmin, canManageSettings])

  if (isLoading) {
    return (
      <div className="set-layout" data-testid="settings-loading">
        <nav className="set-nav" aria-label="Settings navigation">
          {navItems.map((item) => (
            <button key={item.id} className="set-nav-item" disabled>
              <item.Icon size={16} aria-hidden="true" />
              <span className="set-nav-label">{item.label}</span>
            </button>
          ))}
        </nav>
        <main className="set-panels" aria-label="Settings panels">
          <div className="set-skeleton">Loading settings…</div>
        </main>
      </div>
    )
  }

  return (
    <div className="set-layout" data-testid="settings-screen">
      <nav className="set-nav" aria-label="Settings navigation">
        {navItems.map((item) => (
          <button
            key={item.id}
            className={`set-nav-item${effectiveId === item.id ? ' active' : ''}`}
            onClick={() => setActiveId(item.id)}
            aria-current={effectiveId === item.id ? 'location' : undefined}
            data-testid={`settings-nav-${item.id}`}
          >
            <item.Icon size={16} aria-hidden="true" />
            <span className="set-nav-label">{item.label}</span>
          </button>
        ))}
      </nav>

      <main className="set-panels" aria-label="Settings panels">
        {effectiveId === 'company' && (
          <CompanyPanel settings={settings ?? null} onSaved={() => { refetch() }} />
        )}
        {effectiveId === 'team' && (
          <TeamPanel />
        )}
        {effectiveId === 'contact' && (
          <ContactPanel settings={settings ?? null} onSaved={() => { refetch() }} />
        )}
        {effectiveId === 'owners' && (
          <OwnersPanel />
        )}
        {effectiveId === 'branding' && (
          <LogoSignaturePanel
            settings={settings ?? null}
            onSaved={(saved) => {
              queryClient.setQueryData(['settings'], saved)
              void refetch()
            }}
          />
        )}
        {effectiveId === 'defaults' && (
          <ProjectDefaultsPanel settings={settings ?? null} onSaved={() => { refetch() }} />
        )}
        {effectiveId === 'cash' && (
          <CashPanel settings={settings ?? null} onSaved={() => { refetch() }} />
        )}
        {effectiveId === 'payments' && (
          <PaymentsPanel />
        )}
        {effectiveId === 'billing' && (
          <SubscriptionPanel />
        )}
        {effectiveId === 'integrations' && (
          <IntegrationsPanel />
        )}
        {effectiveId === 'notifications' && (
          <NotificationsPanel />
        )}
        {effectiveId === 'accounting' && (
          <AccountingPanel />
        )}
        {effectiveId === 'admin' && isAdmin && (
          <AdminPanel />
        )}
        {effectiveId === 'account' && (
          <AccountPanel />
        )}
      </main>
    </div>
  )
}
